import { useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { SHIPPING_AGENCY_TRANSFER_STATUS } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/flow/utils";
import type { RequestDeparture } from "@services/departure/types";
import { createDepartureRequest } from "@services/departureServiceApi";
import { getShippingAgencyTransfers } from "@services/shippingAgencyTransferApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import type { DepartureAvailability } from "../Departure/availability/departureAvailability";
import { FORM_FIELDS } from "../Departure/constants";
import mapRequestDepartureFormToPayload from "../Departure/mappers/requestDepartureMapper";
import { useRequestDepartureFormStore } from "../Departure/store/useRequestDepartureForm.store";

type useCreateDepartureRequestProps = {
  vesselCallId: string;
  resetForm: () => void;
  refetchDepartureDetails?: () => void;
  refetchDepartureAvailability?: () => Promise<DepartureAvailability | undefined>;
};

const LANG_BASE = "vessel-call.request-departure.notifications";
const LANG_BASE_TRANSFER = "vessel-call.shipping-agency-transfer.notifications";

// This error is sent from BE if Departure fails due to disputes validation.
const DEPARTURE_DISPUTE_ERROR = "DEPARTURE_DISPUTE_ERROR";

export const useCreateDepartureRequest = (props: useCreateDepartureRequestProps) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { clearFilesForKey } = useUploadStore();
  const [error, setError] = useState<string | null>(null);

  const { data: pendingTransfers } = useQuery({
    queryKey: ["pendingShippingAgencyTransfers", props.vesselCallId],
    queryFn: () =>
      getShippingAgencyTransfers("bff-vessel-call", {
        vesselCallId: props.vesselCallId ?? "",
        pageNumber: 1,
        pageSize: 1,
        statuses: [
          SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_APPROVAL,
          SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_MARINE_DEPARTMENT_APPROVAL,
        ],
      }),
    enabled: !!props.vesselCallId,
    staleTime: 0,
    refetchOnMount: true,
  });

  const mutation = useMutation({
    mutationFn: (payload: RequestDeparture) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "RequestDeparture",
          action: "submit_departure_request",
          entityType: "departure_request",
        },
        (transactionId) =>
          createDepartureRequest(payload, ...(transactionId ? [transactionId] : [])),
      ),
    onSuccess: () => {
      setError(null);
      props.resetForm();
      clearFilesForKey(FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION);
      clearFilesForKey(FORM_FIELDS.CARGO_MANIFEST);
      clearFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS);
      notify({
        title: t(`${LANG_BASE}.request-submitted-success-title`),
        description: t(`${LANG_BASE}.request-submitted-success-description`),
        type: "success",
      });
      props.refetchDepartureDetails?.();
    },
    onError: (err: any) => {
      void props.refetchDepartureAvailability?.();
      const IS_DEPARTURE_DISPUTE_ERROR =
        err?.status === 400 && err?.response?.data?.detail === DEPARTURE_DISPUTE_ERROR;

      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description:
          (IS_DEPARTURE_DISPUTE_ERROR
            ? t(`${LANG_BASE}.departure-disputes-error-title`)
            : undefined) ??
          err?.response?.data?.message ??
          err?.message ??
          t(`${LANG_BASE}.request-submitted-error-description`),
        type: "error",
      });
    },
  });

  const validateBeforeConfirm = async () => {
    const hasPendingTransfer = (pendingTransfers?.shippingAgencyTransfers?.length ?? 0) > 0;

    if (hasPendingTransfer) {
      setError(t(`${LANG_BASE_TRANSFER}.active-shipping-agency-transfer-error`));
      return false;
    }

    setError(null);
    return true;
  };

  const onSubmit = async () => {
    setError(null);
    const state = useRequestDepartureFormStore.getState();
    const payload = mapRequestDepartureFormToPayload(props?.vesselCallId, state);
    mutation.mutate(payload);
  };

  return { onSubmit, error, validateBeforeConfirm };
};
